import { useEffect, useRef, useState } from "react";
import { Send, AudioLines, PhoneOff, Paperclip, Square, Loader2, FileText, TriangleAlert, X, Sparkles } from "lucide-react";
import { voiceLiveCopy } from "@/lib/voiceStage";
import { ATTACH_ACCEPT, attachStatusLabel, fileKind } from "@/lib/attach";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";

// How many text rows the textarea grows to before it starts scrolling itself.
const MAX_ROWS = 4;

// Shared thumb-friendly composer with >=44px touch targets (NFR2) and a 16px
// textarea so iOS never auto-zooms on focus.
//
// Layout is responsive:
//   • <md (mobile): textarea spans the full width on top; a second row holds the
//     controls — attach on the left, mic + send on the right.
//   • >=md (desktop): everything sits on one line (attach, textarea, mic, send),
//     exactly as before, via `display: contents` + `order` on the control groups.
//
// Attach and mic slots only render when their handlers are provided, so text-only
// surfaces stay lean.
export default function ChatComposer({
  value,
  onChange,
  onSend,
  onStop,
  sending = false,
  queued = false,
  disabled = false,
  placeholder = "Escreva aqui...",
  rows = 1,
  inputRef,
  onAttach,
  attaching = false,
  onFiles,
  attachItems = [],
  threadDocs = [],
  onUseDoc,
  onDetachDoc,
  fileInputRef,
  attachAccept = ATTACH_ACCEPT,
  onMicToggle,
  micDisabled = false,
  voiceActive = false,
  voiceStatus = "listening",
  testids = {},
}) {
  const t = {
    input: testids.input || "composer-input",
    send: testids.send || "composer-send",
    attach: testids.attach || "composer-attach",
    voice: testids.voice || "composer-voice",
  };

  const localInputRef = useRef(null);
  const taRef = inputRef || localInputRef;
  const localFileRef = useRef(null);
  const fileRef = fileInputRef || localFileRef;
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const canAttach = Boolean(onFiles || onAttach);
  const inFlight = attachItems.filter((i) => i.status !== "done");
  const inFlightNames = new Set(inFlight.map((i) => i.name));
  const readyDocs = threadDocs.filter((d) => !inFlightNames.has(d.filename));
  const liveCopy = voiceActive ? voiceLiveCopy(voiceStatus) : null;

  const openAttach = () => {
    if (onFiles) fileRef.current?.click();
    else onAttach?.();
  };

  const handleDrop = (e) => {
    if (!onFiles || disabled) return;
    e.preventDefault();
    setDragOver(false);
    onFiles(e.dataTransfer?.files);
  };

  // Auto-grow from 1 row up to MAX_ROWS, then scroll inside the textarea. Runs on
  // every value change (send clears it back to a single row).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    const cs = window.getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || 22;
    const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const borderY = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const max = line * MAX_ROWS + padY + borderY;
    el.style.height = `${Math.min(el.scrollHeight, max)}px`;
    el.style.overflowY = el.scrollHeight > max ? "auto" : "hidden";
  }, [value, taRef]);

  // Enter durante o streaming NÃO é ignorado: onSend enfileira a mensagem e ela sai
  // sozinha quando a resposta terminar. Antes, o guard silencioso engolia o texto.
  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <div
      className={`relative border-t border-brand-line px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:px-4 lg:pt-4 lg:pb-[max(1rem,env(safe-area-inset-bottom))] ${
        dragOver ? "bg-brand-rose/10" : ""
      }`}
      onDragEnter={(e) => { if (onFiles && !disabled) { e.preventDefault(); setDragOver(true); } }}
      onDragOver={(e) => { if (onFiles && !disabled) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      {onFiles && (
        <input
          ref={fileRef}
          type="file"
          accept={attachAccept}
          multiple
          className="hidden"
          data-testid={t.attach + "-input"}
          onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }}
        />
      )}
      {dragOver && (
        <div
          data-testid="attach-drop-overlay"
          className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-t-2xl border-2 border-dashed border-brand-rose text-[13px] font-ui text-brand-rose"
        >
          Solte aqui para anexar
        </div>
      )}
      {(readyDocs.length > 0 || inFlight.length > 0) && (
        <div className="mb-2.5 flex flex-wrap gap-1.5" data-testid="attach-strip">
          {readyDocs.map((doc) => (
            <span
              key={doc.id || doc.filename}
              data-testid="attach-pill"
              className="inline-flex items-center max-w-full rounded-full border border-brand-sage/40 bg-brand-sage/10 text-[11.5px] font-ui text-brand-sage min-h-[32px]"
            >
              <button
                type="button"
                onClick={() => setPreview(doc)}
                title="Ver o que a ValiosIA leu"
                className="inline-flex items-center gap-1.5 pl-2.5 pr-1 py-1 hover:text-brand-ink transition-colors min-h-[32px]"
              >
                <FileText className="w-3 h-3 shrink-0" />
                <span className="truncate max-w-[140px]">{doc.filename}</span>
                <span className="opacity-70">{fileKind(doc.filename)}</span>
              </button>
              {onUseDoc && (
                <button
                  type="button"
                  data-testid="attach-use"
                  onClick={() => onUseDoc(doc)}
                  title="Usar agora"
                  aria-label={`Usar ${doc.filename}`}
                  className="px-1.5 py-1 hover:text-brand-ink min-h-[32px]"
                >
                  <Sparkles className="w-3 h-3" />
                </button>
              )}
              {onDetachDoc && (
                <button
                  type="button"
                  data-testid="attach-detach"
                  onClick={() => onDetachDoc(doc)}
                  title="Tirar desta conversa"
                  aria-label={`Tirar ${doc.filename}`}
                  className="pr-2 pl-0.5 py-1 hover:text-brand-rose min-h-[32px]"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </span>
          ))}
          {inFlight.map((item) => (
            <span
              key={item.id}
              data-testid={`attach-item-${item.status}`}
              className={`inline-flex items-center gap-1.5 max-w-full rounded-full border px-2.5 py-1 text-[11.5px] font-ui min-h-[32px] ${
                item.status === "error"
                  ? "border-brand-rose/40 bg-brand-rose/10 text-brand-rose"
                  : "border-brand-line bg-brand-elevated text-brand-ink2"
              }`}
            >
              {item.status === "error" ? (
                <TriangleAlert className="w-3 h-3 shrink-0" />
              ) : (
                <Loader2 className="w-3 h-3 shrink-0 animate-spin" />
              )}
              <span className="truncate max-w-[200px]">{attachStatusLabel(item)}</span>
            </span>
          ))}
        </div>
      )}
      <Sheet open={Boolean(preview)} onOpenChange={(o) => !o && setPreview(null)}>
        <SheetContent
          side="bottom"
          data-testid="attach-preview"
          className="bg-brand-surface border-brand-line rounded-t-3xl max-h-[80vh] overflow-y-auto overflow-x-hidden scrollbar-soft"
        >
          <SheetHeader className="text-left">
            <SheetTitle className="font-display text-2xl text-brand-ink truncate">
              {preview?.filename}
            </SheetTitle>
          </SheetHeader>
          <p className="mt-1 text-[12.5px] text-brand-ink2 font-ui">
            O que a ValiosIA leu deste arquivo
            {preview?.truncated ? " (texto cortado no limite)" : ""}.
          </p>
          <pre className="mt-4 whitespace-pre-wrap break-words text-[13.5px] leading-relaxed text-brand-ink font-sans">
            {preview?.text || "Sem texto extraído."}
          </pre>
          {onUseDoc && preview && (
            <button
              type="button"
              data-testid="attach-preview-use"
              onClick={() => { onUseDoc(preview); setPreview(null); }}
              className="mt-5 pill-pink px-5 py-2.5 text-[13px] font-ui inline-flex items-center gap-2"
            >
              <Sparkles className="w-3.5 h-3.5" /> Usar agora
            </button>
          )}
        </SheetContent>
      </Sheet>
      {queued && (
        <p
          data-testid="composer-queued"
          className="mb-2 text-[12px] text-brand-ink2"
          aria-live="polite"
        >
          Sua mensagem vai sair assim que a ValiosIA terminar de responder ✦
        </p>
      )}
      {voiceActive && onMicToggle && (
        <div
          data-testid="voice-live-bar"
          className="mb-3 flex items-center gap-3 rounded-2xl border border-brand-sage/35 bg-brand-sage/10 px-3.5 py-2.5"
          role="status"
          aria-live="polite"
        >
          <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden>
            <span className="absolute inset-0 rounded-full bg-brand-sage/70 animate-ping" />
            <span className="relative h-2.5 w-2.5 rounded-full bg-brand-sage" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[12.5px] font-ui text-brand-ink">{liveCopy.title}</div>
            <div className="text-[11px] text-brand-ink2">{liveCopy.hint}</div>
          </div>
          <button
            type="button"
            data-testid={(t.voice || "composer-voice") + "-end"}
            onClick={onMicToggle}
            disabled={disabled}
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-brand-rose text-white text-[12.5px] font-ui min-h-[40px] hover:bg-brand-roseHover disabled:opacity-40"
          >
            <PhoneOff className="w-3.5 h-3.5" />
            Encerrar
          </button>
        </div>
      )}
      <div className="flex flex-col gap-2 md:flex-row md:items-end md:gap-3">
        <textarea
          ref={taRef}
          data-testid={t.input}
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="order-1 md:order-2 w-full md:flex-1 min-w-0 px-4 py-3 rounded-2xl bg-brand-elevated border border-brand-line focus:border-brand-rose focus:outline-none resize-none text-[16px] leading-[1.4] text-white placeholder:text-brand-ink3 disabled:opacity-60 disabled:cursor-not-allowed"
        />
        {/* Controls: own row on mobile; dissolves into the flex line on desktop. */}
        <div className="order-2 flex items-center gap-2 md:contents">
          {canAttach && (
            <button
              type="button"
              data-testid={t.attach}
              onClick={openAttach}
              disabled={disabled}
              title="Anexar PDF, DOCX, TXT, CSV, XLSX ou imagem. Pode soltar vários."
              aria-label="Anexar documento"
              className="md:order-1 shrink-0 w-11 h-11 flex items-center justify-center rounded-full bg-brand-elevated text-brand-ink2 border border-brand-line hover:border-brand-rose hover:text-brand-rose transition-colors disabled:opacity-40"
            >
              {attaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
            </button>
          )}
          <div className="flex items-center gap-2 ml-auto md:contents">
            {onMicToggle && !voiceActive && (
              <button
                type="button"
                data-testid={t.voice}
                onClick={onMicToggle}
                disabled={disabled || micDisabled}
                title="Iniciar conversa ao vivo"
                aria-label="Iniciar conversa ao vivo"
                aria-pressed={false}
                className="md:order-3 shrink-0 w-11 h-11 flex items-center justify-center rounded-full transition-colors border bg-brand-elevated text-brand-rose border-brand-line hover:border-brand-rose disabled:opacity-40"
              >
                <AudioLines className="w-4 h-4" />
              </button>
            )}
            {/* Durante o streaming o botão vira Parar — nunca fica inerte, que era
                a leitura de "cliquei e não aconteceu nada". */}
            {sending && onStop ? (
              <button
                type="button"
                data-testid="composer-stop"
                onClick={onStop}
                disabled={disabled}
                aria-label="Parar resposta"
                title="Parar resposta"
                className="md:order-4 shrink-0 w-11 h-11 flex items-center justify-center bg-brand-elevated border border-brand-line text-brand-ink hover:border-brand-rose hover:text-brand-rose rounded-full transition-colors"
              >
                <Square className="w-3.5 h-3.5 fill-current" />
              </button>
            ) : (
              <button
                type="button"
                data-testid={t.send}
                onClick={onSend}
                disabled={disabled}
                aria-label="Enviar"
                className="md:order-4 shrink-0 w-11 h-11 flex items-center justify-center bg-brand-rose hover:bg-brand-roseHover text-white rounded-full disabled:opacity-50 transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
